import { useState, useCallback } from "react";
import { API, G, GL, GB, BLACK, MID, LIGHT, BORDER, BG, BENCHMARKS, calcRevenue } from "../constants";
import { Ring, Tag, ProgressBar, BigRing } from "../components/shared";
import { PageHead, Footer } from "../components/Shell";
import { SOURCE_META } from "../data";

export function S07({ appr, fixes, onReset, analyticsConnected, setAnalyticsConnected, setRealCVR, setRealAOV, realCVR, realAOV, products, generatedCopy = {}, pushResults = null, demoMode = false, shopConnected = false }) {
  const REAL_ANALYTICS = { cvr: 0.011, aov: 138 };
  const [analyticsConnecting, setAnalyticsConnecting] = useState(false);

  const handleConnectAnalytics = () => {
    setAnalyticsConnecting(true);
    setTimeout(() => {
      setAnalyticsConnecting(false);
      setAnalyticsConnected(true);
      setRealCVR(REAL_ANALYTICS.cvr);
      setRealAOV(REAL_ANALYTICS.aov);
    }, 1800);
  };
  const prods    = products.filter(p => appr.includes(p.id));
  const totFix   = Object.values(fixes).filter(Boolean).length;
  const totSig   = prods.reduce((s, p) => s + (p.signalAttribution?.length || 0), 0);
  const totRev   = prods.reduce((s, p) => s + p.impact.revenue, 0);
  const totImp   = prods.reduce((s, p) => s + (p.impact.impAfter - p.impact.impBefore), 0);
  const totClk   = prods.reduce((s, p) => s + (p.impact.clkAfter - p.impact.clkBefore), 0);

  const scoreBefore = 41;
  const scoreAfter  = Math.min(100, 41
    + (prods.length >= 1 ? 12 : 0)
    + (totFix > 0 ? 10 : 0)
    + (totSig > 0 ? 8 : 0)
    + (prods.length >= 2 ? 4 : 0)
  );
  const scoreDelta  = scoreAfter - scoreBefore;
  const scoreColor  = n => n >= 70 ? G : n >= 50 ? "#d97706" : "#dc2626";

  const [animated, setAnimated] = useState(false);
  useState(() => { const t = setTimeout(() => setAnimated(true), 400); return () => clearTimeout(t); });

  // Push result helpers
  const pushSuccess = pushResults?.success;
  const pushCount = pushResults?.pushed ?? 0;
  const pushTotal = pushResults?.total ?? prods.length;
  const pushTime = pushResults?.pushed_at ? new Date(pushResults.pushed_at).toLocaleString() : null;
  const isDemo = pushResults?.demo || demoMode || !shopConnected;
  const perSkuResults = pushResults?.results || [];

  return (
    <>
      {/* Hero */}
      <div data-testid="done-hero" style={{ textAlign: "center", padding: "40px 0 28px" }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: pushSuccess !== false ? GL : "#fef2f2",
          border: `2px solid ${pushSuccess !== false ? GB : "#fecaca"}`,
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 24
        }}>
          {pushSuccess !== false ? "\u2713" : "\u2717"}
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8, letterSpacing: "-0.4px" }}>
          {pushSuccess !== false ? "Changes are live." : "Push encountered errors."}
        </h1>
        <p style={{ fontSize: 14, color: MID, margin: "0 auto 0", maxWidth: 480, lineHeight: 1.65 }}>
          {pushSuccess !== false
            ? `${pushCount} SKU${pushCount !== 1 ? "s" : ""} pushed to Shopify${isDemo ? " (demo mode)" : ""} and Google Merchant Centre.`
            : `${pushCount} of ${pushTotal} SKU${pushTotal !== 1 ? "s" : ""} pushed successfully. See details below.`
          }
        </p>
        {pushTime && (
          <p style={{ fontSize: 12, color: LIGHT, marginTop: 6 }}>
            Pushed at {pushTime}{pushResults?.batch_id ? ` \u00b7 Batch ${pushResults.batch_id}` : ""}
          </p>
        )}
      </div>

      {/* Push Results Per SKU */}
      {perSkuResults.length > 0 && (
        <div data-testid="push-results" style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ padding: "12px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>
              Push results {isDemo && <span style={{ fontSize: 10, fontWeight: 700, color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 3, padding: "1px 6px", marginLeft: 6 }}>Demo mode</span>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: G, background: GL, border: `1px solid ${GB}`, borderRadius: 4, padding: "2px 8px" }}>
                {pushCount} pushed
              </span>
              {pushTotal - pushCount > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, padding: "2px 8px" }}>
                  {pushTotal - pushCount} failed
                </span>
              )}
            </div>
          </div>
          {perSkuResults.map((r, i) => (
            <div key={r.id || i} data-testid={`push-result-${r.id}`} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 18px",
              borderBottom: i < perSkuResults.length - 1 ? `1px solid ${BORDER}` : "none",
              background: r.success ? BG : "#fffafa",
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                background: r.success ? GL : "#fef2f2",
                border: `1.5px solid ${r.success ? GB : "#fecaca"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 800, color: r.success ? G : "#dc2626",
              }}>
                {r.success ? "\u2713" : "\u2717"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{r.title || `Product ${r.id}`}</div>
                {r.success && r.changes && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                    {r.changes.map(c => (
                      <span key={c} style={{ fontSize: 10, fontWeight: 600, color: G, background: GL, border: `1px solid ${GB}`, borderRadius: 3, padding: "1px 6px" }}>
                        {c.replace(/_/g, " ").replace(/^gmc:/, "GMC: ")}
                      </span>
                    ))}
                  </div>
                )}
                {!r.success && r.error && (
                  <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{r.error}</div>
                )}
                {r.metafield_errors && r.metafield_errors.length > 0 && (
                  <div style={{ fontSize: 11, color: "#d97706", marginTop: 3 }}>
                    Metafield warnings: {r.metafield_errors.join(", ")}
                  </div>
                )}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4,
                color: r.success ? G : "#dc2626",
                background: r.success ? GL : "#fef2f2",
                border: `1px solid ${r.success ? GB : "#fecaca"}`,
              }}>
                {r.success ? "Pushed" : "Failed"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Demo mode banner */}
      {isDemo && (
        <div data-testid="demo-push-banner" style={{
          padding: "12px 18px", marginBottom: 20, borderRadius: 8,
          background: "#fffbeb", border: "1px solid #fde68a",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 14 }}>&#9888;</span>
          <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
            <strong>Demo mode</strong> - no changes were pushed to a real Shopify store. Connect your store in Step 01 to push optimised copy, meta fields and GMC fixes directly to your product listings.
          </div>
        </div>
      )}

      {/* Feed Score Recap */}
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ padding: "12px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Feed score - before vs after</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 0 }}>
          {/* Before */}
          <div style={{ padding: "24px 20px", background: "#fef9f9", textAlign: "center", borderRight: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 12 }}>Before</div>
            <BigRing score={scoreBefore} animated={true} trackColor="#fecaca" />
            <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>Poor</div>
            <div style={{ fontSize: 11, color: LIGHT, marginTop: 3 }}>47 SKUs - 8 issues</div>
          </div>

          {/* Delta */}
          <div style={{ padding: "24px 24px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#fafafa", borderRight: `1px solid ${BORDER}`, minWidth: 110 }}>
            <div style={{ fontSize: 24, color: LIGHT, marginBottom: 8 }}>&rarr;</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: G, lineHeight: 1 }}>+{scoreDelta}</div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.6px", marginTop: 4 }}>pts</div>
          </div>

          {/* After */}
          <div style={{ padding: "24px 20px", background: GL, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 12 }}>After</div>
            <BigRing score={animated ? scoreAfter : scoreBefore} animated={animated} trackColor={GB} />
            <div style={{ fontSize: 12, fontWeight: 700, color: G }}>{scoreAfter >= 70 ? "Good" : "Needs work"}</div>
            <div style={{ fontSize: 11, color: MID, marginTop: 3 }}>{appr.length} SKU{appr.length !== 1 ? "s" : ""} optimised</div>
          </div>
        </div>

        {/* Per-SKU score strip */}
        {prods.length > 0 && (
          <div style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 18px", background: BG, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {prods.map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", border: `1px solid ${GB}`, borderRadius: 6, background: GL }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{p.title}</span>
                <Ring score={p.impact.scoreBefore} size={28} />
                <span style={{ fontSize: 11, color: LIGHT }}>&rarr;</span>
                <Ring score={p.impact.scoreAfter} size={28} />
                <span style={{ fontSize: 11, fontWeight: 800, color: G }}>+{p.impact.scoreAfter - p.impact.scoreBefore}pts</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expected Results */}
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ padding: "12px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Expected results - next 30 days</div>
        </div>

        {/* Big 4 stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
          {[
            { label: "Revenue uplift / mo",    val: `\u00a3${totRev.toLocaleString()}`,      color: G,         icon: "\u00a3", sub: `${(BENCHMARKS.CVR*100).toFixed(1)}% CVR x \u00a3${BENCHMARKS.AOV} AOV`,        note: "Est. incremental" },
            { label: "Extra impressions / mo", val: `+${totImp.toLocaleString()}`,       color: "#4285f4", icon: "eye", sub: "GSC baseline vs projected",                                                note: "Search visibility" },
            { label: "Extra clicks / mo",      val: `+${totClk.toLocaleString()}`,       color: "#6c63ff", icon: "up", sub: `Blended SEO + Shopping CTR`,                                               note: "Traffic uplift" },
            { label: "Signals embedded",       val: totSig,                              color: "#f59e0b", icon: "diamond", sub: `across ${appr.length} SKU${appr.length !== 1 ? "s" : ""}`,                note: "Consumer phrases" },
          ].map((x, i) => (
            <div key={x.label} style={{ padding: "20px 16px", borderRight: i < 3 ? `1px solid ${BORDER}` : "none", textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 900, color: x.color, marginBottom: 4, letterSpacing: "-0.5px" }}>{x.val}</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{x.label}</div>
              <div style={{ fontSize: 10, color: LIGHT, marginBottom: 2 }}>{x.sub}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: x.color, background: `${x.color}12`, borderRadius: 3, padding: "1px 6px", display: "inline-block" }}>{x.note}</div>
            </div>
          ))}
        </div>

        {/* Per-SKU breakdown */}
        {prods.length > 0 && (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            <div style={{ padding: "10px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.7px" }}>Breakdown per SKU</div>
            </div>
            {prods.map((p, i) => (
              <div key={p.id} style={{ display: "grid", gridTemplateColumns: "1fr repeat(4, auto)", gap: 0, alignItems: "center", borderBottom: i < prods.length - 1 ? `1px solid ${BORDER}` : "none", padding: "12px 18px" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{p.title}</div>
                  <div style={{ fontSize: 11, color: LIGHT }}>{p.sku}</div>
                </div>
                {[
                  { label: "Impressions",  val: `+${(p.impact.impAfter - p.impact.impBefore).toLocaleString()}`,  color: "#4285f4" },
                  { label: "Clicks",       val: `+${(p.impact.clkAfter - p.impact.clkBefore).toLocaleString()}`,  color: "#6c63ff" },
                  { label: "Revenue / mo", val: `\u00a3${p.impact.revenue.toLocaleString()}`,                          color: G },
                  { label: "Feed score",   val: `${p.impact.scoreBefore} \u2192 ${p.impact.scoreAfter}`,               color: G },
                ].map(stat => (
                  <div key={stat.label} style={{ textAlign: "right", paddingLeft: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: stat.color }}>{stat.val}</div>
                    <div style={{ fontSize: 10, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Benchmark disclosure */}
        <div style={{ padding: "10px 18px", background: "#fffbeb", borderTop: `1px solid #fde68a`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>Projections based on industry benchmarks:</span>
          {[
            { label: "AOV",     val: `\u00a3${BENCHMARKS.AOV}`,                          tip: "UK DTC avg (IRP Commerce 2025)" },
            { label: "CVR",     val: `${(BENCHMARKS.CVR*100).toFixed(1)}%`,          tip: "DTC session CVR (IRP/Shopify)" },
            { label: "SEO CTR", val: `${(BENCHMARKS.SEO_CTR*100).toFixed(0)}%`,      tip: "Organic pos.1 CTR (Sistrix/Ahrefs 2025)" },
            { label: "PLA CTR", val: `${(BENCHMARKS.PLA_CTR*100).toFixed(2)}%`,      tip: "Shopping CTR (Bind Media 2025)" },
          ].map(b => (
            <span key={b.label} title={b.tip} style={{ fontSize: 11, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 4, padding: "2px 8px", cursor: "help" }}>
              {b.label}: <strong>{b.val}</strong>
            </span>
          ))}
          <span style={{ fontSize: 11, color: "#92400e", fontStyle: "italic", marginLeft: "auto" }}>In production, these use your Shopify store data</span>
        </div>
      </div>

      {/* Export outputs */}
      {prods.length > 0 && <ExportPanel prods={prods} analyticsConnected={analyticsConnected} handleConnectAnalytics={handleConnectAnalytics} analyticsConnecting={analyticsConnecting} realCVR={realCVR} realAOV={realAOV} generatedCopy={generatedCopy} />}

      {/* Feed Intelligence Dashboard (Priority 6) */}
      {prods.length > 0 && <IntelligenceDashboard prods={prods} fixes={fixes} generatedCopy={generatedCopy} />}

      {/* Channels + next scan */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 10 }}>
            {isDemo ? "Would push to" : "Pushed to"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {[
              { label: "Shopify Product Fields",   icon: "S", note: "Title, description, tags updated", color: "#96bf48" },
              { label: "Shopify SEO Meta",         icon: "M", note: "Meta title + description set",    color: "#4285f4" },
              { label: "Shopify Metafields",       icon: "F", note: "SIFT namespace - optimised copy stored", color: "#6c63ff" },
              { label: "Google Merchant Centre",   icon: "G", note: "GMC attributes fixed via metafields", color: "#ea4335" },
              { label: "Google Shopping",          icon: "P", note: "Title + attributes synced to feed", color: "#34a853" },
            ].map(ch => (
              <div key={ch.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: `${ch.color}15`, border: `1px solid ${ch.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: ch.color, flexShrink: 0 }}>
                  {ch.icon}
                </div>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{ch.label}</span>
                  <span style={{ fontSize: 11, color: LIGHT, marginLeft: 6 }}>{ch.note}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ border: `1px solid #fde68a`, borderRadius: 8, padding: "14px 16px", background: "#fffbeb", flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 8 }}>Next signal scan</div>
            <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.6, marginBottom: 10 }}>In 24 hours - SIFT will flag new consumer demand and identify SKUs that have drifted below score threshold.</div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e", background: "#fef3c7", border: `1px solid #fde68a`, padding: "3px 10px", borderRadius: 4 }}>Auto-scan ON</span>
          </div>
          <div style={{ textAlign: "center" }}>
            <button data-testid="run-another-batch-btn" onClick={onReset} style={{ width: "100%", border: `1px solid ${BORDER}`, background: BG, borderRadius: 6, padding: "10px 24px", fontSize: 13, fontWeight: 600, color: MID, cursor: "pointer", fontFamily: "inherit" }}>Run another batch &rarr;</button>
          </div>
        </div>
      </div>
    </>
  );
}

// Export Panel
function ExportPanel({ prods, analyticsConnected, handleConnectAnalytics, analyticsConnecting, realCVR, realAOV, generatedCopy = {} }) {
  const [copied, setCopied] = useState(null);
  const getT = (p) => generatedCopy[p.id]?.optimised_title || p.optimisedTitle || p.title;
  const getD = (p) => generatedCopy[p.id]?.optimised_description || p.optimisedDesc || p.description || "";
  const copyToClipboard = (pid) => {
    const p = prods.find(x => x.id === pid);
    const text = [
      `SKU: ${p.sku}`,
      `Title: ${getT(p)}`,
      `Description: ${getD(p)}`,
      `Meta title: ${generatedCopy[p.id]?.meta_title || getT(p) + " | " + p.brand}`,
      `Tags: ${[...(p.shopifyData?.tags || [])].join(", ")}`,
      `Signals used: ${(p.signalAttribution || []).map(s => s.phrase).join(", ")}`,
    ].join("\n\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(pid);
      setTimeout(() => setCopied(null), 2000);
    });
  };
  const downloadCSV = () => {
    const header = "SKU,Title,Description,Meta Title,Signals Used";
    const rows = prods.map(p =>
      [p.sku, `"${getT(p)}"`, `"${getD(p).replace(/"/g,'""').replace(/\n/g,' ')}"`, `"${generatedCopy[p.id]?.meta_title || getT(p) + " | " + p.brand}"`, `"${(p.signalAttribution||[]).map(s=>s.phrase).join(', ')}"`].join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href = url; a.download = "sift-feed-output.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="export-panel" style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "12px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Export optimised copy</div>
        <button data-testid="download-csv-btn" onClick={downloadCSV} style={{ background: G, color: "#fff", border: "none", borderRadius: 5, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.2px" }}>
          Download CSV
        </button>
      </div>
      <div style={{ padding: "12px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        {prods.map(p => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", border: `1px solid ${BORDER}`, borderRadius: 7, background: BG }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: BLACK, marginBottom: 2 }}>{p.title} <span style={{ fontWeight: 400, color: LIGHT, fontSize: 11 }}>{p.sku}</span></div>
              <div style={{ fontSize: 11, color: MID, maxWidth: 480, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{getT(p)}</div>
            </div>
            <button onClick={() => copyToClipboard(p.id)}
              style={{ background: copied === p.id ? GL : BG, color: copied === p.id ? G : MID, border: `1px solid ${copied === p.id ? GB : BORDER}`, borderRadius: 5, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.2s" }}>
              {copied === p.id ? "\u2713 Copied" : "Copy copy"}
            </button>
          </div>
        ))}
      </div>
      {/* Shopify Analytics CTA */}
      {!analyticsConnected ? (
        <div style={{ borderTop: `1px solid #fde68a`, padding: "12px 18px", background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Revenue projections are using industry benchmarks (CVR 0.8%, AOV \u00a3120). </span>
            <span style={{ fontSize: 12, color: "#92400e" }}>Connect your Shopify Analytics to replace with your real store conversion rate and AOV.</span>
          </div>
          <button onClick={handleConnectAnalytics} disabled={analyticsConnecting}
            style={{ background: analyticsConnecting ? "#b45309" : "#92400e", color: "#fff", border: "none", borderRadius: 5, padding: "7px 14px", fontSize: 11, fontWeight: 700, cursor: analyticsConnecting ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: analyticsConnecting ? 0.8 : 1 }}>
            {analyticsConnecting ? "Connecting\u2026" : "Connect Analytics"}
          </button>
        </div>
      ) : (
        <div style={{ borderTop: `1px solid ${GB}`, padding: "12px 18px", background: GL, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14 }}>{"\u2705"}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: G }}>Using real store data \u2014 </span>
          <span style={{ fontSize: 12, color: G }}>CVR {(realCVR * 100).toFixed(1)}% - AOV \u00a3{realAOV} - Projections reflect your actual store performance</span>
        </div>
      )}
    </div>
  );
}


// ── Feed Intelligence Dashboard (Priority 6) ──────────────────────────────
function IntelligenceDashboard({ prods, fixes, generatedCopy }) {
  const [tab, setTab] = useState("signals");
  const tabs = [
    { id: "signals",    label: "Signal Attribution" },
    { id: "attributes", label: "Attribute Coverage" },
    { id: "keywords",   label: "Keyword Gaps Closed" },
    { id: "channels",   label: "Channel Readiness" },
  ];

  // ── Compute aggregated data ───────────────────────
  // Signal attribution: count how many phrases came from each source
  const sourceCount = {};
  const sourcePhrases = {};
  prods.forEach(p => {
    (p.signalAttribution || []).forEach(sig => {
      (sig.sources || []).forEach(src => {
        sourceCount[src] = (sourceCount[src] || 0) + 1;
        if (!sourcePhrases[src]) sourcePhrases[src] = [];
        if (!sourcePhrases[src].includes(sig.phrase)) sourcePhrases[src].push(sig.phrase);
      });
    });
  });
  const sourceRanked = Object.entries(sourceCount).sort((a, b) => b[1] - a[1]);
  const totalSignals = sourceRanked.reduce((s, [, c]) => s + c, 0);

  // Attribute coverage: aggregate V/I/C counts
  let attrV = 0, attrI = 0, attrC = 0;
  const attrRows = [];
  prods.forEach(p => {
    (p.attributeMatches || []).forEach(a => {
      if (a.status === "verified") attrV++;
      else if (a.status === "inferred") attrI++;
      else if (a.status === "conflict") attrC++;
      attrRows.push({ ...a, product: p.title, sku: p.sku });
    });
  });
  const attrTotal = attrV + attrI + attrC;

  // Keyword gaps: find signals used that weren't in the original title/description
  const gapPhrases = [];
  prods.forEach(p => {
    const origText = `${p.shopifyData?.title || p.title} ${p.shopifyData?.tags?.join(" ") || ""}`.toLowerCase();
    (p.signalAttribution || []).forEach(sig => {
      if (!origText.includes(sig.phrase.toLowerCase())) {
        gapPhrases.push({ phrase: sig.phrase, sources: sig.sources, product: p.title, sku: p.sku, note: sig.note });
      }
    });
  });

  // Channel readiness
  const channelData = prods.map(p => {
    const copy = generatedCopy[p.id] || {};
    const hasTitle = !!(copy.optimised_title || p.optimisedTitle);
    const hasDesc = !!(copy.optimised_description || p.optimisedDesc);
    const hasMeta = !!(copy.meta_title);
    const hasGmc = (p.gmcIssues || []).length > 0 && Object.keys(fixes).length > 0;
    const hasAttrs = (p.attributeMatches || []).filter(a => a.status === "verified").length >= 3;
    return {
      title: p.title, sku: p.sku,
      shopping: hasTitle && hasAttrs && hasGmc ? "ready" : hasTitle ? "partial" : "gap",
      seo: hasTitle && hasDesc && hasMeta ? "ready" : hasTitle && hasDesc ? "partial" : "gap",
      aeo: hasDesc && hasAttrs ? "ready" : hasDesc ? "partial" : "gap",
    };
  });

  const statusIcon = (s) => s === "ready" ? "\u2713" : s === "partial" ? "\u25CB" : "\u2717";
  const statusColor = (s) => s === "ready" ? G : s === "partial" ? "#d97706" : "#dc2626";
  const statusBg = (s) => s === "ready" ? GL : s === "partial" ? "#fffbeb" : "#fef2f2";

  return (
    <div data-testid="intelligence-dashboard" style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
      {/* Header */}
      <div style={{ padding: "12px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Feed Intelligence Dashboard</div>
        <div style={{ display: "flex", gap: 1, background: BORDER, borderRadius: 5, overflow: "hidden" }}>
          {tabs.map(t => (
            <button key={t.id} data-testid={`intel-tab-${t.id}`} onClick={() => setTab(t.id)} style={{
              background: tab === t.id ? "#fff" : "#f5f5f5",
              border: "none", padding: "5px 12px", fontSize: 11, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? BLACK : LIGHT, cursor: "pointer", fontFamily: "inherit",
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* ── Tab: Signal Attribution ── */}
      {tab === "signals" && (
        <div data-testid="intel-signals-tab" style={{ padding: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 24 }}>
            {/* Left: source breakdown bars */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Source contribution</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sourceRanked.slice(0, 8).map(([src, count]) => {
                  const meta = SOURCE_META[src] || { label: src.toUpperCase(), color: LIGHT, bg: "#f5f5f5" };
                  const pct = totalSignals > 0 ? Math.round((count / totalSignals) * 100) : 0;
                  return (
                    <div key={src}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                        <span style={{ fontSize: 10, color: LIGHT }}>{count} ({pct}%)</span>
                      </div>
                      <div style={{ height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: meta.color, borderRadius: 3, transition: "width 0.6s ease" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Right: phrase breakdown per source */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Phrases by source</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto" }}>
                {sourceRanked.slice(0, 6).map(([src]) => {
                  const meta = SOURCE_META[src] || { label: src.toUpperCase(), color: LIGHT, bg: "#f5f5f5" };
                  const phrases = sourcePhrases[src] || [];
                  return (
                    <div key={src} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderBottom: `1px solid ${BORDER}` }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, background: meta.bg, borderRadius: 3, padding: "2px 6px", flexShrink: 0, minWidth: 36, textAlign: "center" }}>{meta.label}</span>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {phrases.slice(0, 6).map(ph => (
                          <span key={ph} style={{ fontSize: 10, color: MID, background: "#f5f5f5", borderRadius: 3, padding: "2px 6px" }}>{ph}</span>
                        ))}
                        {phrases.length > 6 && <span style={{ fontSize: 10, color: LIGHT }}>+{phrases.length - 6} more</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Summary strip */}
              <div style={{ display: "flex", gap: 12, marginTop: 14, padding: "8px 12px", background: GL, borderRadius: 6, border: `1px solid ${GB}` }}>
                <div style={{ fontSize: 10, color: G }}><strong>{totalSignals}</strong> signal-source pairs</div>
                <div style={{ fontSize: 10, color: G }}><strong>{sourceRanked.length}</strong> active sources</div>
                <div style={{ fontSize: 10, color: G }}><strong>{Object.values(sourcePhrases).reduce((s, p) => s + p.length, 0)}</strong> unique phrases</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Attribute Coverage ── */}
      {tab === "attributes" && (
        <div data-testid="intel-attributes-tab" style={{ padding: 18 }}>
          {/* VIC summary */}
          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            {[
              { label: "Verified", count: attrV, color: G, bg: GL, border: GB, desc: "Signal matches Shopify data" },
              { label: "Inferred", count: attrI, color: "#d97706", bg: "#fffbeb", border: "#fde68a", desc: "Signal suggests missing attribute" },
              { label: "Conflict", count: attrC, color: "#dc2626", bg: "#fef2f2", border: "#fecaca", desc: "Signal contradicts store data" },
            ].map(v => (
              <div key={v.label} style={{ flex: 1, padding: "14px 16px", borderRadius: 8, border: `1px solid ${v.border}`, background: v.bg, textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: v.color, lineHeight: 1 }}>{v.count}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: v.color, marginTop: 4 }}>{v.label}</div>
                <div style={{ fontSize: 10, color: LIGHT, marginTop: 2 }}>{v.desc}</div>
              </div>
            ))}
          </div>
          {/* Coverage bar */}
          {attrTotal > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ height: 8, borderRadius: 4, overflow: "hidden", display: "flex", background: "#f0f0f0" }}>
                <div style={{ width: `${(attrV / attrTotal) * 100}%`, background: G, transition: "width 0.5s" }} />
                <div style={{ width: `${(attrI / attrTotal) * 100}%`, background: "#f59e0b", transition: "width 0.5s" }} />
                <div style={{ width: `${(attrC / attrTotal) * 100}%`, background: "#dc2626", transition: "width 0.5s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: 10, color: LIGHT }}>{attrTotal} attributes mapped across {prods.length} SKUs</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: G }}>{attrTotal > 0 ? Math.round((attrV / attrTotal) * 100) : 0}% verified</span>
              </div>
            </div>
          )}
          {/* Attribute rows */}
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {attrRows.map((a, i) => {
              const sc = a.status === "verified" ? G : a.status === "inferred" ? "#d97706" : "#dc2626";
              const sbg = a.status === "verified" ? GL : a.status === "inferred" ? "#fffbeb" : "#fef2f2";
              return (
                <div key={`${a.product}-${a.field}-${i}`} style={{ display: "grid", gridTemplateColumns: "100px 80px 1fr 80px", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: BLACK }}>{a.field}</span>
                  <span style={{ fontSize: 10, color: LIGHT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.sku}</span>
                  <span style={{ fontSize: 10, color: MID }}>{a.shopifyVal || "Missing"} {a.signalMatch ? `\u2192 ${a.signalMatch}` : ""}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: sc, background: sbg, borderRadius: 3, padding: "2px 8px", textAlign: "center", textTransform: "capitalize" }}>{a.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tab: Keyword Gaps Closed ── */}
      {tab === "keywords" && (
        <div data-testid="intel-keywords-tab" style={{ padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLACK, marginBottom: 4 }}>Phrases discovered and embedded that were absent from the original listing</div>
          <div style={{ fontSize: 10, color: LIGHT, marginBottom: 14 }}>{gapPhrases.length} keyword gaps closed across {prods.length} SKUs</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
            {gapPhrases.map((g, i) => (
              <div key={`${g.phrase}-${i}`} style={{ display: "grid", gridTemplateColumns: "140px 80px 1fr auto", gap: 8, alignItems: "center", padding: "8px 12px", border: `1px solid ${BORDER}`, borderRadius: 6, background: BG }}>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: BLACK }}>{g.phrase}</span>
                </div>
                <span style={{ fontSize: 10, color: LIGHT }}>{g.sku}</span>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {g.sources.map(src => {
                    const meta = SOURCE_META[src] || { label: src, color: LIGHT, bg: "#f5f5f5" };
                    return <span key={src} style={{ fontSize: 9, fontWeight: 700, color: meta.color, background: meta.bg, borderRadius: 3, padding: "1px 5px" }}>{meta.label}</span>;
                  })}
                </div>
                <span style={{ fontSize: 10, color: MID, fontStyle: "italic", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.note}</span>
              </div>
            ))}
            {gapPhrases.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: LIGHT, fontSize: 12 }}>No keyword gaps detected - all signal phrases were already present in the original listings.</div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Channel Readiness ── */}
      {tab === "channels" && (
        <div data-testid="intel-channels-tab" style={{ padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLACK, marginBottom: 14 }}>Per-SKU readiness across Shopping, SEO, and AEO channels</div>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 110px 110px", gap: 0, padding: "8px 12px", background: "#fafafa", borderRadius: "6px 6px 0 0", border: `1px solid ${BORDER}`, borderBottom: "none" }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px" }}>SKU</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>Google Shopping</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>SEO / Organic</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>AEO / LLM</span>
          </div>
          {/* Rows */}
          {channelData.map((ch, i) => (
            <div key={ch.sku} style={{ display: "grid", gridTemplateColumns: "1fr 110px 110px 110px", gap: 0, padding: "10px 12px", border: `1px solid ${BORDER}`, borderTop: "none", borderRadius: i === channelData.length - 1 ? "0 0 6px 6px" : 0 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{ch.title}</div>
                <div style={{ fontSize: 10, color: LIGHT }}>{ch.sku}</div>
              </div>
              {["shopping", "seo", "aeo"].map(c => (
                <div key={c} style={{ textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                  <span style={{ width: 20, height: 20, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: statusColor(ch[c]), background: statusBg(ch[c]), border: `1.5px solid ${statusColor(ch[c])}30` }}>
                    {statusIcon(ch[c])}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: statusColor(ch[c]), textTransform: "capitalize" }}>{ch[c]}</span>
                </div>
              ))}
            </div>
          ))}
          {/* Summary */}
          <div style={{ display: "flex", gap: 12, marginTop: 14, padding: "8px 12px", background: GL, borderRadius: 6, border: `1px solid ${GB}` }}>
            {["shopping", "seo", "aeo"].map(c => {
              const ready = channelData.filter(ch => ch[c] === "ready").length;
              return (
                <div key={c} style={{ fontSize: 10, color: G }}>
                  <strong>{c === "aeo" ? "AEO" : c === "seo" ? "SEO" : "Shopping"}</strong>: {ready}/{channelData.length} ready
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
